"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PortalModal } from "@/components/ui/portal-modal";
import { devAddresses } from "@/lib/localDevAddresses";
import { Wallet, Plus } from "lucide-react";

interface LocalWalletModalProps {
  open: boolean;
  onSelect: (address: string) => void;
  onClose: () => void;
}

export const LocalWalletModal = ({ open, onSelect, onClose }: LocalWalletModalProps) => {
  const [customAddress, setCustomAddress] = useState("");
  const [isValidAddress, setIsValidAddress] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
      setCustomAddress("");
      setIsValidAddress(false);
    }
  };

  const handleSelect = (address: string) => {
    onSelect(address);
    onClose();
    setCustomAddress("");
    setIsValidAddress(false);
  };

  const handleCustomAddressChange = (value: string) => {
    setCustomAddress(value);
    // Basic Ethereum address validation
    const isValid = /^0x[a-fA-F0-9]{40}$/.test(value);
    setIsValidAddress(isValid);
  };

  const handleCustomAddressSubmit = () => {
    if (isValidAddress && customAddress) {
      handleSelect(customAddress);
    }
  };

  return (
    <PortalModal
      open={open}
      onOpenChange={handleOpenChange}
      showCloseButton
      className="w-full sm:w-[700px] max-w-[700px] max-h-[90vh] gap-6 rounded-large border border-[var(--color-connect-account-border)] bg-[var(--color-connect-account-bg)] p-6 text-left shadow-[0_20px_120px_-40px_rgba(0,0,0,0.7)] sm:p-8 z-modal"
    >
      <PortalModal.Header className="gap-3 text-left">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cede-primary/10 text-cede-primary">
            <Wallet className="size-5" />
          </div>
          <div className="flex flex-1 items-center gap-2">
            <PortalModal.Title className="h5 text-cede-text-primary sm:h6">Select Dev Wallet</PortalModal.Title>
            <Badge variant="warning" size="small" className="uppercase tracking-wide text-xs">
              Local
            </Badge>
          </div>
        </div>
        <PortalModal.Description className="body2 text-cede-text-secondary">
          Choose a development wallet address. Pre-seeded addresses (#1-#10) or add your own custom address.
        </PortalModal.Description>
      </PortalModal.Header>

      <PortalModal.Content scrollable className="flex flex-col gap-4 min-h-0">
        <div className="flex flex-col gap-2">
          {devAddresses.map((address, index) => (
            <Button
              key={address}
              type="button"
              variant="outline"
              size="medium"
              className="w-full justify-start gap-4 rounded-large border border-cede-border-component bg-transparent px-4 py-3 font-mono text-sm text-cede-text-primary transition hover:border-cede-primary hover:bg-cede-primary/5 focus-visible:border-cede-primary focus-visible:ring-2 focus-visible:ring-cede-primary/40"
              onClick={() => handleSelect(address)}
            >
              <span className="w-16 text-left font-semibold text-cede-text-secondary">#{index + 1}</span>
              <span className="flex-1 text-left break-all">{address}</span>
            </Button>
          ))}
        </div>

        <div className="border-t border-cede-border-component pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="size-4 text-cede-text-secondary" />
            <span className="text-sm font-medium text-cede-text-primary">Add Custom Address</span>
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="0x..."
              value={customAddress}
              onChange={(e) => handleCustomAddressChange(e.target.value)}
              className={`flex-1 font-mono text-sm ${
                customAddress && !isValidAddress ? "border-cede-error" : isValidAddress ? "border-cede-success" : ""
              }`}
            />
            <Button
              type="button"
              variant="outline"
              size="medium"
              disabled={!isValidAddress || !customAddress}
              onClick={handleCustomAddressSubmit}
              className="px-4"
            >
              Connect
            </Button>
          </div>
          {customAddress && !isValidAddress && (
            <p className="text-xs text-red-500 mt-1">Please enter a valid Ethereum address</p>
          )}
        </div>
      </PortalModal.Content>

      <PortalModal.Footer className="justify-center">
        <Button
          type="button"
          variant="transparent"
          className="w-full justify-center text-cede-text-secondary hover:text-cede-text-primary sm:w-auto"
          onClick={onClose}
        >
          Cancel
        </Button>
      </PortalModal.Footer>
    </PortalModal>
  );
};
